/**
 * What the signed-in member may see and change, from the permissions their
 * access level resolves to (the server's App\Access\PermissionCatalog, sent by
 * GetStatus as _ext.PERMISSIONS with each key's ordered values in
 * _ext.PERMISSION_VALUES).
 *
 * Nothing here knows a level by its code: a level the Founder creates, or one
 * he changes, behaves exactly as its permissions say. A member with no level,
 * or a level that no longer exists, gets every key's most restrictive value
 * from the server, so here they can do almost nothing.
 *
 * The property rules mirror backend/app/Services/Access/PropertyEditLocks.php
 * (the plan's "Property lock mapping") — change both together.
 *
 * CommonJS (not ESM) so the rules can be unit-tested with the node-backend
 * test harness; Babel's interop lets the React components `import` it.
 */

var accessPermissions = require('./access-permissions');
var routeAccess = require('./route-access');

// ── The access object ─────────────────────────────────────────────────────────

function normalizeEmail(value) {
    return String(value == null ? '' : value).trim().toLowerCase();
}

/** Whether `access` gives `key` at least `atLeast` ('yes' for a toggle). Anything unknown is refused. */
function accessCan(access, key, atLeast) {
    return accessPermissions.can(access && access.permissions, access && access.permissionValues, key, atLeast);
}

/**
 * The signed-in member's access.
 *
 * @param {object} perms     {permissions, values, levelCode, levelName, isFounder} from GetStatus _ext
 * @param {object} identity  {email, memberId} — memberId is the TEAM ARCIMEDES_ROW_ID
 */
function buildAccess(perms, identity) {
    var p = perms || {};
    var who = identity || {};
    var access = {
        email: normalizeEmail(who.email),
        memberId: who.memberId == null || who.memberId === '' ? null : String(who.memberId),
        levelCode: p.levelCode || null,
        levelName: p.levelName || null,
        // Whether the member's level is the protected (Founder) one. The server
        // enforces what only the Founder may do; this only hides what it would refuse.
        isFounder: !!p.isFounder,
        permissions: p.permissions || {},
        permissionValues: p.values || {},
    };
    var has = function (key, atLeast) { return accessCan(access, key, atLeast); };

    // Shorthands pages already read; each is one permission.
    access.summitPipeline = has('summit.agent_pipeline', 'all') ? 'full' : (has('summit.agent_pipeline', 'own') ? 'own' : 'none');
    access.proposals = has('proposals', 'edit') ? 'edit' : (has('proposals', 'view') ? 'view' : 'none');
    access.uploads = has('uploads');
    access.peakPipeline = has('peak.pipeline');
    access.peakStats = has('peak.stats');
    access.editPeakSections = has('property.peak_sections', 'edit');
    access.deleteTraining = has('training.delete');
    // A choice of landing page (home.landing), not a permission: what Root sends the member to after signing in.
    access.landingPage = (access.permissions || {})['home.landing'] || null;
    access.teamList = has('team.list');
    access.manageTeamAccounts = has('team.accounts', 'edit');
    access.assignAccessLevel = has('access.manage');

    return access;
}

/**
 * Whether the member edits every SUMMIT record: property records at edit and
 * the SUMMIT scope at all. This is what the old "super" role meant on the
 * property page, the lists and the record pages.
 */
function editsEverySummitRecord(access) {
    return accessCan(access, 'properties.records', 'edit') && accessCan(access, 'property.summit_scope', 'all');
}

/**
 * Whether the member edits only the SUMMIT deals they are agent on: property
 * records at edit and the SUMMIT scope at own (the old "senior agent" role).
 */
function editsOwnSummitDealsOnly(access) {
    return accessCan(access, 'properties.records', 'edit')
        && !accessCan(access, 'property.summit_scope', 'all')
        && accessCan(access, 'property.summit_scope', 'own');
}

/**
 * Whether a property in a list links to its page: the member's level opens
 * property records, and one limited to their own SUMMIT deals sees links only
 * on External and SUMMIT properties that are not Off Market (today's rule).
 */
function canLinkToProperty(access, property) {
    if (!accessCan(access, 'properties.records', 'view')) return false;
    if (accessCan(access, 'property.summit_scope', 'all')) return true;
    var source = property && property.SOURCE;
    return (source === 'External' || source === 'SUMMIT') && property.STATUS !== 'Off Market';
}

/** Whether a Summit Pipeline sidebar link is shown: when its page opens (route-access.js). */
function canSeeSummitPipelineLink(access, key) {
    var path = routeAccess.SUMMIT_PIPELINE_LINK_ROUTES[key];
    return !!path && routeAccess.canOpenRoute(function (k, atLeast) { return accessCan(access, k, atLeast); }, path);
}

/**
 * Whether a team member's account is the Founder's to change and not the
 * viewer's: it holds a Founder-only level (Founder, Admin) and the viewer is
 * neither the Founder nor that member. The server refuses the rest.
 *
 * @param {Array} levelOptions  the access-levels options ({code, founder_only})
 * @param {*} memberLevel       the member's ACCESS_LEVEL
 * @param {*} memberId          the member's TEAM ARCIMEDES_ROW_ID
 */
function isFounderOnlyAccount(access, levelOptions, memberLevel, memberId) {
    var code = (memberLevel ? String(memberLevel).trim().toLowerCase() : '') || '';
    var held = (levelOptions || []).filter(function (o) { return o.code === code; })[0];
    var a = access || {};

    return !a.isFounder && !!held && !!held.founder_only && String(a.memberId) !== String(memberId);
}

/** The training module "All but Brokerage Continuum" keeps closed (training.edit). */
var TRAINING_CONTINUUM_SLUG = 'brokerage-continuum';

/**
 * Whether the member edits the training pages under `topSlug` (the module in
 * the URL: training/<topSlug>/...). Yes edits every module; "All but Brokerage
 * Continuum" every module but that one.
 */
function canEditTraining(access, topSlug) {
    if (accessCan(access, 'training.edit', 'yes')) return true;
    return accessCan(access, 'training.edit', 'except_continuum') && topSlug !== TRAINING_CONTINUUM_SLUG;
}

/** Agent Pipeline pages show only the signed-in agent's own figures. */
function isOwnSummitPipelineOnly(access) {
    return accessCan(access, 'summit.agent_pipeline', 'own') && !accessCan(access, 'summit.agent_pipeline', 'all');
}

/** Agent Commission and Paid & Procured show only the signed-in agent's own figures. */
function isOwnCommissionOnly(access) {
    return accessCan(access, 'summit.commission', 'own') && !accessCan(access, 'summit.commission', 'all');
}

/**
 * What the viewer may do on a team member's page.
 *
 * @returns {{forms: boolean, accountSettings: 'edit'|'view'|null, territories: 'edit'|'view',
 *            info: 'edit'|'view', accountFields: 'edit'|'view'}}
 *   accountFields covers EMAIL, TERMINATION_DATE and Role (IS_AGENT) on the Team Member tab.
 */
function getTeamMemberTabAccess(access, teamMemberEmail, isNew) {
    if (isNew) {
        return { forms: true, accountSettings: 'edit', territories: 'edit', info: 'edit', accountFields: 'edit' };
    }

    var a = access || {};
    var subject = normalizeEmail(teamMemberEmail);
    var isSelf = !!a.email && a.email === subject;
    var accountsEdit = accessCan(a, 'team.accounts', 'edit');
    var accountsShown = accessCan(a, 'team.accounts', 'view') && (isSelf || accessCan(a, 'team.list'));

    return {
        forms: accessCan(a, 'team.forms', 'all') || (isSelf && accessCan(a, 'team.forms', 'own')),
        accountSettings: accountsShown ? (accountsEdit ? 'edit' : 'view') : null,
        territories: accessCan(a, 'team.territories', 'edit') ? 'edit' : 'view',
        info: accessCan(a, 'team.info', 'edit') ? 'edit' : 'view',
        accountFields: accountsEdit ? 'edit' : 'view',
    };
}

// ── Property page ─────────────────────────────────────────────────────────────

var STATUS_ACTIVE = 'Active';
var STATUS_IN_NEGOTIATION = 'In Negotiation';
var STATUS_UNDER_CONTRACT = 'Under Contract';
var STATUS_CLOSED = 'Closed';
var STATUS_PROPOSAL = 'Proposal';
var SOURCE_SUMMIT = 'SUMMIT';
var SOURCE_PEAK = 'HSC';

/** Listing Agent and Buyside Agent tabs. */
var AGENT_PERCENT_FIELDS = [
    'LISTING_AGENT_1_PERCENT', 'LISTING_AGENT_2_PERCENT', 'LISTING_AGENT_3_PERCENT', 'LISTING_AGENT_4_PERCENT',
    'REFERRING_LISTING_AGENT_PERCENT',
    'BUYER_AGENT_1_PERCENT', 'BUYER_AGENT_2_PERCENT', 'BUYER_AGENT_3_PERCENT', 'BUYER_AGENT_4_PERCENT',
    'REFERRING_BUYER_AGENT_PERCENT',
];

/** Who the listing agents are (PropertyEditLocks::LISTING_AGENT_COLUMNS). */
var LISTING_AGENT_FIELDS = [
    'LISTING_AGENT_1', 'LISTING_AGENT_2', 'LISTING_AGENT_3', 'LISTING_AGENT_4', 'LISTING_AGENT_4_FIRM', 'REFERRING_LISTING_AGENT',
];

/** Who the buyer agents are (PropertyEditLocks::BUYER_AGENT_COLUMNS). */
var BUYER_AGENT_FIELDS = [
    'BUYER_AGENT_1', 'BUYER_AGENT_2', 'BUYER_AGENT_3', 'BUYER_AGENT_4', 'BUYER_AGENT_4_FIRM', 'REFERRING_BUYER_AGENT',
];

/** Rep Agreement tab: Date Listed, # Of Months, Listing Expiration Date, Fee %. */
var REP_AGREEMENT_FIELDS = ['LISTED_DATE', 'EXPIRATION_NO_OF_MONTHS', 'EXPIRATION_DATE', 'SELLER_FEE_PERCENTAGE'];

/** In Neg/Under Contract tab prices. */
var IN_NEGOTIATION_PRICE_FIELDS = ['NEGOTIATION_PRICE', 'NEGOTIATION_CAP_RATE'];

/** Sale Info tab — what a closing records. */
var CLOSING_FIELDS = [
    'CLOSE_OF_ESCROW', 'SOLD_PRICE', 'ESTIMATED_PRICE_TYPE', 'CLOSE_NOI', 'CLOSE_CAP_RATE',
    'CLOSE_PRICE_PER_SQFT', 'DOWN_PAYMENT', 'LOAN_AMOUNT', 'NET_FEE', 'REFERRAL_FEE',
    'LIST_FEE', 'BUYSIDE_FEE', 'TOTAL_CLOSING_FEE_AUTO', 'SELLER_STATEMENT_FILE', 'BUYER_STATEMENT_FILE',
];

/** Loan Info tab columns that do not carry the HSC_ prefix. */
var LOAN_INFO_FIELDS = [
    'LENDER_ID', 'LENDER_CONTACT_ID', 'LOAN_TYPE', 'LOAN_AMOUNT', 'INTEREST_RATE', 'AMORTIZATION', 'TERM', 'MATURITY',
    'MATURITY_DATE', 'PREPAYMENT_PENALTY', 'LTV', 'DSCR', 'CASH_FLOW', 'CASH_ON_CASH',
    'ANNUAL_LOAN_PAYMENT', 'LOAN_CLOSING_DATE', 'LOAN_COMMITMENT_FILE', 'CLOSING_STATEMENT_FILE',
    'CREDITS_AT_CLOSING', 'BANK_BONUS', 'BANK_BONUS_PERCENT',
];

/** Loan Info's referral block — Peak's only while the deal is Peak-financed. */
var PEAK_REFERRAL_FIELDS = ['REFERRAL', 'REFERRAL_PERCENT', 'REFERRED_BY_ID'];

/** Peak columns that are not Loan Info's (PropertyEditLocks::NOT_LOAN_INFO_PEAK_COLUMNS). */
var NOT_LOAN_INFO_PEAK_FIELDS = ['HSC_FINANCING', 'HSC_STATUS', 'HSC_PRICE'];

/** The Peak payments' own figures: shown read-only at "Loans only"; the server accepts them because a save recomputes them from the team member's %. */
var PEAK_PAYMENT_FIELD_PATTERN = /^HSC_TEAM_MEMBER_\d+_(GROSS_FEE|NET_FEE|NET_PERCENT)$/;

/** Stamped on every save (PropertyEditLocks::MODIFIED_STAMP_COLUMNS). */
var MODIFIED_STAMP_FIELDS = ['LAST_MODIFIED_DATE', 'LAST_MODIFIED_BY'];

/** Every property tab but the two a closed SUMMIT record at "Loans only" leaves open. */
var TABS_CLOSED_AT_LOANS_ONLY = [
    'PROPERTY', 'INVESTORS', 'FINANCIALS', 'SALE INFO', 'BUYSIDE AGENT', 'LISTING AGENT', 'PAYMENT',
    'PROPOSAL', 'REP AGREEMENT', 'IN NEG/UNDER CONTRACT', 'HISTORY', 'WEBSITE',
];

/**
 * The Financials tab's own inputs (PropertyEditLocks::FINANCIALS_COLUMNS), locked without
 * property.financials on edit. The auto figures beside them (PSF, Rent/Sq Ft, Price/Unit)
 * stay unlocked: they follow the building size, which any editor may change.
 */
var FINANCIALS_FIELDS = [
    'HIDE_FINANCIALS', 'IS_PROFORMA_PRICING', 'HSC_PRICE', 'LIST_PRICE',
    'CAPITALIZATION_RATE', 'PROFORMA_CAP_RATE', 'NET_OPERATING_INCOME', 'PROFORMA_NOI',
    'BASE_RENT', 'PROFORMA_BASE_RENT', 'UNITS', 'VACANCY',
];

/** Worked out from the List Price; held back with it. */
var PRICE_DERIVED_FIELDS = ['PRICE_PER_SQFT', 'PRICE_PER_UNIT', 'TOTAL_FEE'];

/** Every Peak column on a property starts with this (HSC_FINANCING, HSC_STATUS, HSC_TEAM_MEMBER_1, ...). */
var PEAK_FIELD_PREFIX = 'HSC_';

/** The SUMMIT statuses a 'limited' deleter may still delete at. */
var EARLY_STAGE_STATUSES = ['Proposal', 'Property Info'];

/** Statuses on which a List Price change may be typed but is not saved without property.list_price_live. */
var PRICE_HELD_STATUSES = [STATUS_ACTIVE, STATUS_IN_NEGOTIATION, STATUS_UNDER_CONTRACT];

var READ_ONLY_RECORDS = 'Your access level lets you view this property but not change it.';
var READ_ONLY_PEAK = 'Your access level cannot change Peak financing records.';
var READ_ONLY_CLOSED_SUMMIT = 'Your access level cannot change a closed SUMMIT property.';
var CLOSED_SUMMIT_LOANS_ONLY = 'Your access level can change only the Loan Quotes and Loan Info of a closed SUMMIT property.';
var LIST_PRICE_HELD = 'Your access level cannot change the List Price while a property is Active, In Negotiation or Under Contract — it was put back.';

function isTruthyFlag(value) {
    if (value === true || value === 1) return true;
    var s = String(value == null ? '' : value).trim().toLowerCase();
    return s === '1' || s === 'y' || s === 'yes' || s === 'true';
}

function toMap(list) {
    var map = {};
    for (var i = 0; i < list.length; i++) map[list[i]] = true;
    return map;
}

function noLocks() {
    return {
        readOnly: false,
        readOnlyReason: null,
        hard: {},
        hardPrefixes: [],
        soft: {},
        softReason: null,
        blockedValues: {},
        only: null,
        readOnlyTabs: {},
        extraTabs: [],
        hidePeakTracking: false,
        peakPaymentsReadOnly: false,
        financialsReadOnly: false,
    };
}

/** Without property.financials on edit: the Financials tab and its spreadsheet are view-only. */
function lockFinancials(access, locks) {
    if (accessCan(access, 'property.financials', 'edit')) return locks;
    locks.financialsReadOnly = true;
    locks.readOnlyTabs.FINANCIALS = true;
    return locks;
}

function readOnlyLocks(reason) {
    var locks = noLocks();
    locks.readOnly = true;
    locks.readOnlyReason = reason;
    return locks;
}

/**
 * A closed SUMMIT record at "Loans only" (PropertyEditLocks::loansOnly): the
 * Edit button stays, every field is locked but Loan Info's, and every tab but
 * Loan Quotes and Loan Info is shown in view mode. Needs the Peak sections on edit.
 */
function loansOnlyLocks(o) {
    var locks = noLocks();
    var peakFinanced = isTruthyFlag(o.HSC_FINANCING);
    var fields = MODIFIED_STAMP_FIELDS.slice();
    if (peakFinanced || isTruthyFlag(o.OTHER_FINANCING)) fields = fields.concat(LOAN_INFO_FIELDS);
    if (peakFinanced) fields = fields.concat(PEAK_REFERRAL_FIELDS);

    locks.readOnlyReason = CLOSED_SUMMIT_LOANS_ONLY;
    locks.only = { fields: toMap(fields), prefixes: [PEAK_FIELD_PREFIX], except: NOT_LOAN_INFO_PEAK_FIELDS };
    for (var i = 0; i < TABS_CLOSED_AT_LOANS_ONLY.length; i++) locks.readOnlyTabs[TABS_CLOSED_AT_LOANS_ONLY[i]] = true;
    locks.peakPaymentsReadOnly = true;
    return locks;
}

/** Whether `key` is outside what a "Loans only" lock leaves open. */
function isOutsideOnly(only, key) {
    if (only.fields[key]) return false;
    if (only.except.indexOf(key) !== -1 || PEAK_PAYMENT_FIELD_PATTERN.test(String(key))) return true;
    for (var i = 0; i < only.prefixes.length; i++) {
        if (String(key).indexOf(only.prefixes[i]) === 0) return false;
    }
    return true;
}

/**
 * Which property fields the viewer may not change, worked out from the record
 * as it was loaded (`original`) so that changing STATUS in the same edit
 * cannot lift a limit that depends on it.
 *
 *   readOnly       nothing on the record may change; the Edit button is hidden.
 *   hard           fields that are shown disabled.
 *   hardPrefixes   every field starting with one of these is disabled.
 *   only           when set, every field outside it is disabled ("Loans only").
 *   soft           fields that stay editable but are put back on Save (the
 *                  client's "can alter but not save").
 *   blockedValues  { FIELD: [values] } a field may not be set to.
 *   readOnlyTabs   { TAB NAME: true } tabs shown in view mode.
 *   extraTabs      tabs an own-deals agent sees beyond the ones they always had.
 *
 * A record being created has no limits; creating needs properties.create.
 */
function getPropertyEditLocks(access, original) {
    var o = original || {};
    // A record being created has no other limits, but its Financials are held back the same way.
    if (!o.ARCIMEDES_ROW_ID) {
        var fresh = lockFinancials(access, noLocks());
        if (fresh.financialsReadOnly) fresh.hard = toMap(FINANCIALS_FIELDS);
        return fresh;
    }

    var has = function (key, atLeast) { return accessCan(access, key, atLeast); };
    var isSummit = o.SOURCE === SOURCE_SUMMIT;
    var isEarly = EARLY_STAGE_STATUSES.indexOf(o.STATUS) !== -1;

    if (!has('properties.records', 'edit')) return readOnlyLocks(READ_ONLY_RECORDS);
    if (o.SOURCE === SOURCE_PEAK && !has('property.peak_records', 'edit')) return readOnlyLocks(READ_ONLY_PEAK);
    var closedSummit = isSummit && o.STATUS === STATUS_CLOSED;
    if (closedSummit && !has('property.summit_closings', 'loans')) return readOnlyLocks(READ_ONLY_CLOSED_SUMMIT);
    if (closedSummit && !has('property.summit_closings')) {
        return has('property.peak_sections', 'edit') ? lockFinancials(access, loansOnlyLocks(o)) : readOnlyLocks(READ_ONLY_CLOSED_SUMMIT);
    }

    var locks = noLocks();
    var hard = [];
    var blockedStatus = [];

    if (!has('property.agent_percents')) hard = hard.concat(AGENT_PERCENT_FIELDS);
    if (!has('property.rep_agreement_terms')) hard = hard.concat(REP_AGREEMENT_FIELDS);
    if (!has('property.in_neg_price')) hard = hard.concat(IN_NEGOTIATION_PRICE_FIELDS);
    if (!has('property.peak_financing')) hard.push('HSC_FINANCING');
    if (!has('property.website_visible')) hard.push('VISIBLE');

    // Loans only changes nothing on a record that is not closed.
    var closingsLocked = isSummit && !has('property.summit_closings', 'loans');
    if (closingsLocked) {
        hard = hard.concat(CLOSING_FIELDS);
        blockedStatus.push(STATUS_CLOSED);
        locks.readOnlyTabs['SALE INFO'] = true;
    }

    var deletesAll = has('properties.delete', 'all');
    if (!has('properties.delete', 'limited')) {
        hard.push('DELETED');
    } else if (isSummit && !deletesAll && !isEarly) {
        // Nor may a live listing be pulled back to an early stage, which would lift that limit.
        hard.push('DELETED');
        blockedStatus = blockedStatus.concat(EARLY_STAGE_STATUSES);
    }

    var agentsLocked = isSummit && !has('property.agents');
    if (agentsLocked) {
        hard = hard.concat(BUYER_AGENT_FIELDS);
        locks.readOnlyTabs['BUYSIDE AGENT'] = true;
        var ownProposal = o.STATUS === STATUS_PROPOSAL && !!(access && access.memberId)
            && o.LISTING_AGENT_1 != null && String(o.LISTING_AGENT_1) === String(access.memberId);
        if (!ownProposal) {
            hard = hard.concat(LISTING_AGENT_FIELDS);
            locks.readOnlyTabs['LISTING AGENT'] = true;
        }
    }

    var ownScope = !has('property.summit_scope', 'all');
    if (isSummit && (closingsLocked || !deletesAll || agentsLocked || ownScope)) {
        // SOURCE is what every SUMMIT limit hangs on.
        hard.push('SOURCE');
    }

    if (blockedStatus.length) locks.blockedValues.STATUS = blockedStatus;

    if (PRICE_HELD_STATUSES.indexOf(o.STATUS) !== -1 && !has('property.list_price_live')) {
        locks.soft = toMap(['LIST_PRICE']);
        locks.softReason = LIST_PRICE_HELD;
    }

    if (!has('property.payments', 'edit')) locks.readOnlyTabs.PAYMENT = true;

    if (lockFinancials(access, locks).financialsReadOnly) hard = hard.concat(FINANCIALS_FIELDS);

    if (!has('property.peak_sections', 'edit')) {
        // The loan columns are shared with Sale Info (a buyer's loan on a
        // closing), so they are Loan Info's only while that tab is shown.
        if (isTruthyFlag(o.HSC_FINANCING) || isTruthyFlag(o.OTHER_FINANCING)) hard = hard.concat(LOAN_INFO_FIELDS);
        if (isTruthyFlag(o.HSC_FINANCING)) hard = hard.concat(PEAK_REFERRAL_FIELDS);
        locks.hardPrefixes = [PEAK_FIELD_PREFIX];
        locks.readOnlyTabs['LOAN QUOTES'] = true;
        locks.readOnlyTabs['LOAN INFO'] = true;
        locks.hidePeakTracking = true;
        locks.peakPaymentsReadOnly = true;
    }

    if (isSummit && ownScope) locks.extraTabs = ['REP AGREEMENT', 'IN NEG/UNDER CONTRACT', 'WEBSITE'];

    locks.hard = toMap(hard);
    return locks;
}

/** True when `key` may not be changed (shown disabled). */
function isFieldLocked(locks, key) {
    if (!locks) return false;
    if (locks.readOnly) return true;
    if (locks.hard[key]) return true;
    if (locks.only && isOutsideOnly(locks.only, key)) return true;
    for (var i = 0; i < locks.hardPrefixes.length; i++) {
        if (String(key).indexOf(locks.hardPrefixes[i]) === 0) return true;
    }
    return false;
}

/** True when setting `key` to `value` is refused outright. */
function isValueBlocked(locks, key, value) {
    if (!locks) return false;
    var blocked = locks.blockedValues[key];
    return !!blocked && blocked.indexOf(value) !== -1;
}

/**
 * The soft-locked fields the edit changed. `same(a, b)` decides equality —
 * the form holds "5,220,000" where the database holds "5220000.00".
 */
function changedSoftLockedFields(locks, data, original, same) {
    var changed = [];
    if (!locks) return changed;
    var keys = Object.keys(locks.soft);
    for (var i = 0; i < keys.length; i++) {
        var k = keys[i];
        if (!same((data || {})[k], (original || {})[k])) changed.push(k);
    }
    return changed;
}

/** True when the signed-in member is one of the property's Summit listing or buyer agents. */
function isAgentOnProperty(teamMemberId, property) {
    if (teamMemberId == null || teamMemberId === '' || !property) return false;
    var id = String(teamMemberId);
    var keys = ['LISTING_AGENT_1', 'LISTING_AGENT_2', 'LISTING_AGENT_3', 'BUYER_AGENT_1', 'BUYER_AGENT_2', 'BUYER_AGENT_3'];
    for (var i = 0; i < keys.length; i++) {
        var v = property[keys[i]];
        if (v != null && v !== '' && String(v) === id) return true;
    }
    return false;
}

module.exports = {
    AGENT_PERCENT_FIELDS: AGENT_PERCENT_FIELDS,
    LISTING_AGENT_FIELDS: LISTING_AGENT_FIELDS,
    BUYER_AGENT_FIELDS: BUYER_AGENT_FIELDS,
    REP_AGREEMENT_FIELDS: REP_AGREEMENT_FIELDS,
    IN_NEGOTIATION_PRICE_FIELDS: IN_NEGOTIATION_PRICE_FIELDS,
    CLOSING_FIELDS: CLOSING_FIELDS,
    LOAN_INFO_FIELDS: LOAN_INFO_FIELDS,
    PEAK_REFERRAL_FIELDS: PEAK_REFERRAL_FIELDS,
    PRICE_DERIVED_FIELDS: PRICE_DERIVED_FIELDS,
    FINANCIALS_FIELDS: FINANCIALS_FIELDS,
    accessCan: accessCan,
    buildAccess: buildAccess,
    editsEverySummitRecord: editsEverySummitRecord,
    editsOwnSummitDealsOnly: editsOwnSummitDealsOnly,
    canSeeSummitPipelineLink: canSeeSummitPipelineLink,
    canLinkToProperty: canLinkToProperty,
    isFounderOnlyAccount: isFounderOnlyAccount,
    isOwnSummitPipelineOnly: isOwnSummitPipelineOnly,
    isOwnCommissionOnly: isOwnCommissionOnly,
    canEditTraining: canEditTraining,
    getTeamMemberTabAccess: getTeamMemberTabAccess,
    getPropertyEditLocks: getPropertyEditLocks,
    isFieldLocked: isFieldLocked,
    isValueBlocked: isValueBlocked,
    changedSoftLockedFields: changedSoftLockedFields,
    isAgentOnProperty: isAgentOnProperty,
};
